/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useState } from 'preact/hooks';
import { useUser } from '#hooks/UNSAFE_useUser';
import type { gestureTypes } from '#hooks/PRIVATE_useVisContextMenu';
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { cancelEvent } from '../../utils/UNSAFE_visUtils/eventsUtils';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

type ItemInfo = { isCurrent?: boolean; isFocusVisible?: boolean };

type EventDetail<D> = { data?: D };

export function useVisEvent<
  I extends ItemInfo,
  J extends EventDetail<D>,
  L extends EventDetail<D>,
  D
>(
  touchResponse: 'touchStart' | 'touchHold',
  isInteractive: boolean,
  isItemActionable: (itemInfo: I) => boolean,
  getKeyUpHandler: (
    triggerOnAction: () => void,
    updatefocusedItemInfo: (itemInfo: I) => void,
    focusedItemInfo: I,
    isHideShow?: boolean
  ) => (event: KeyboardEvent) => void,
  getDetailFromInfo: (info?: I) => J | L,
  getPrevItemInfo: (info: I) => I,
  getNextItemInfo: (info: I) => I,
  onItemAction: ((detail: J) => void) | undefined,
  onItemHover: ((detail: L) => void) | undefined,
  onItemFocus: ((detail: L) => void) | undefined,
  isEqualItem: (item1?: I, item2?: I) => boolean,
  getItemInfo: (element: HTMLElement) => I | undefined,
  initItemInfo: I,
  isHideShow?: boolean
) {
  const activeIdRef = useRef<string>();

  const [focusedItemInfo, setfocusedItemInfo] = useState<I>(initItemInfo);
  const [hoveredItemInfo, sethoveredItemInfo] = useState<I>();

  const onHover = (event: PointerEvent) => {
    const itemInfo = getItemInfo(event.target as HTMLElement);
    if (!isEqualItem(itemInfo, hoveredItemInfo)) {
      sethoveredItemInfo(itemInfo && { ...itemInfo, isCurrent: true });
      activeIdRef.current = getRandomId();
      if (isInteractive) {
        focusedItemInfo ? setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false }) : '';
        onItemHover?.(getDetailFromInfo(itemInfo) as L);
      }
    }
  };

  const onHoverLeave = () => {
    sethoveredItemInfo(undefined);
    activeIdRef.current = undefined;
    if (isInteractive) {
      onItemHover?.(getDetailFromInfo() as L);
    }
  };

  const hoverHandlers = useVisHover(onHover, undefined, onHoverLeave, touchResponse);

  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  if (!isInteractive) {
    return {
      isInteractive: false,
      focusedItemInfo,
      hoveredItemInfo,
      // include relevant hover handlers for datatip
      eventsProps: hoverHandlers
    };
  }

  const updatefocusedItemInfo = (itemInfo: I) => {
    onItemFocus?.(getDetailFromInfo(itemInfo) as L);
    activeIdRef.current = getRandomId();
    setfocusedItemInfo(itemInfo);
  };

  const handleKeyboardInput = (item: I) => {
    if (!isEqualItem(item, focusedItemInfo)) {
      item.isCurrent = true;
      item.isFocusVisible = true;
      if (hoveredItemInfo) {
        sethoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      updatefocusedItemInfo(item);
    }
  };

  const triggerOnAction = () => {
    const detail = getDetailFromInfo(focusedItemInfo) as J;
    if (detail && detail.data && isItemActionable(focusedItemInfo)) {
      onItemAction?.(detail);
      activeIdRef.current = getRandomId();
    }
  };

  const onPointerUp = (event: PointerEvent) => {
    const itemInfo = getItemInfo(event.target as HTMLElement);
    if (itemInfo != null && isItemActionable(itemInfo)) {
      setfocusedItemInfo(itemInfo);
      const detail = getDetailFromInfo(itemInfo);
      if (detail && isItemActionable(itemInfo)) {
        onItemAction?.(detail as J);
        activeIdRef.current = getRandomId();
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown': {
        const nextItemInfo = getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowUp': {
        const nextItemInfo = getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowRight': {
        const nextItemInfo = isRtl
          ? getPrevItemInfo(focusedItemInfo)
          : getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowLeft': {
        const nextItemInfo = isRtl
          ? getNextItemInfo(focusedItemInfo)
          : getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
    }
  };

  const onKeyUp = getKeyUpHandler(
    triggerOnAction,
    updatefocusedItemInfo,
    focusedItemInfo,
    isHideShow
  );

  const onContextMenuDismissed = (gesture: gestureTypes) => {
    if (gesture === 'keyboard') {
      updatefocusedItemInfo({
        ...focusedItemInfo,
        isCurrent: true,
        isFocusVisible: true
      });
    }
  };

  const onBlur = () => {
    const itemInfo = { ...focusedItemInfo, isCurrent: false, isFocusVisible: false } as I;
    if (hoveredItemInfo != null || focusedItemInfo.isCurrent) {
      onItemFocus?.(getDetailFromInfo(undefined) as L);
    }
    setfocusedItemInfo(itemInfo);
  };

  return {
    isInteractive: true,
    focusedItemInfo,
    hoveredItemInfo,
    activeId: activeIdRef.current,
    triggerOnAction,
    updatefocusedItemInfo,
    onContextMenuDismissed,
    eventsProps: mergeProps(hoverHandlers, {
      onPointerUp,
      onKeyUp,
      onKeyDown,
      onBlur
    })
  };
}
